import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchAccountFeaturesOrEmpty, type AccountFeatureRow } from '../lib/backlog/featureAccounts';
import { logWarn } from '../lib/logger';
import { supabase } from '../lib/supabase';
import type { CRMContact, CRMOpportunity, OnboardingLead, CRMPipelineStage } from '../types/crm';
import type { CRMNote } from '../types/notes';
import type { MeetingReport, ExtractedAction } from '../types/meetingReport';
import type { Database, Json } from '../types/supabase';

type ContactRow = Database['public']['Tables']['crm_contacts']['Row'];
type NoteRow = Database['public']['Tables']['crm_notes']['Row'];
type OpportunityRow = Database['public']['Tables']['crm_opportunities']['Row'];
type LeadRow = Database['public']['Tables']['onboarding_leads']['Row'];
type MeetingReportRow = Database['public']['Tables']['crm_meeting_reports']['Row'];

/** Coerce a JSON array from the DB into ExtractedAction[] (defaults to [] for null/non-array). */
function toExtractedActions(value: Json | null): ExtractedAction[] {
  return Array.isArray(value) ? (value as unknown as ExtractedAction[]) : [];
}

/** Narrow a nullable custom_fields JSON into the domain Record shape. */
function toCustomFields(value: Json | null | undefined): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function toCRMContact(row: ContactRow): CRMContact {
  return {
    id: row.id,
    account_id: row.account_id,
    first_name: row.first_name,
    last_name: row.last_name,
    email: row.email,
    phone: row.phone,
    title: row.title,
    status: row.status === 'inactive' ? 'inactive' : 'active',
    owner_id: row.owner_id,
    last_contacted_at: row.last_contacted_at,
    feature_notifications_enabled: row.feature_notifications_enabled ?? false,
    custom_fields: toCustomFields(row.custom_fields),
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/** A crm_notes row optionally joined with a light contact reference. */
type NoteRowWithContact = NoteRow & {
  contact?: { id: string; first_name: string; last_name: string } | null;
};

function toCRMNote(row: NoteRowWithContact): CRMNote {
  return {
    id: row.id,
    title: row.title,
    content: row.content,
    note_type: row.note_type,
    account_id: row.account_id,
    contact_id: row.contact_id,
    meeting_id: row.meeting_id,
    appointment_id: row.appointment_id,
    opportunity_id: row.opportunity_id,
    lead_id: row.lead_id,
    tags: row.tags ?? [],
    is_pinned: row.is_pinned ?? false,
    owner_id: row.owner_id,
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
    source: row.source === 'agent' ? 'agent' : 'manual',
  };
}

/** A crm_pipeline_stages join limited to the columns selected here. */
type StageJoin = { id: string; name: string; color: string | null; order_index: number } | null;

function toCRMOpportunity(row: OpportunityRow & { stage?: StageJoin }): CRMOpportunity {
  const stage: CRMPipelineStage | undefined = row.stage
    ? {
        id: row.stage.id,
        name: row.stage.name,
        color: row.stage.color,
        order_index: row.stage.order_index,
        description: null,
        default_probability: null,
        created_at: '',
        updated_at: '',
      }
    : undefined;
  return {
    id: row.id,
    name: row.name,
    account_id: row.account_id,
    stage_id: row.stage_id,
    amount: row.amount,
    currency: row.currency ?? 'EUR',
    close_date: row.close_date,
    probability: row.probability,
    status: (row.status as CRMOpportunity['status']) ?? 'open',
    owner_id: row.owner_id,
    custom_fields: toCustomFields(row.custom_fields),
    onboarding_lead_id: row.onboarding_lead_id,
    is_onboarding: row.is_onboarding ?? false,
    stripe_product_id: row.stripe_product_id,
    custom_product_name: row.custom_product_name,
    calculated_mrr: row.calculated_mrr,
    calculated_arr: row.calculated_arr,
    revenue_type: (row.revenue_type as CRMOpportunity['revenue_type']) ?? 'recurring',
    one_time_amount: row.one_time_amount,
    target_close_date: row.target_close_date,
    reason_lost: row.reason_lost,
    created_at: row.created_at,
    updated_at: row.updated_at,
    stage,
  };
}

function toOnboardingLead(row: LeadRow): OnboardingLead {
  return {
    id: row.id,
    company_id: row.company_id,
    company_name: row.company_name,
    siret: row.siret,
    address: row.address,
    postal_code: row.postal_code,
    city: row.city,
    country: row.country,
    contact_first_name: row.contact_first_name,
    contact_last_name: row.contact_last_name,
    contact_email: row.contact_email,
    contact_phone: row.contact_phone,
    erp: row.erp,
    erp_other: row.erp_other,
    referral: row.referral,
    referral_other: row.referral_other,
    slot_start: row.slot_start,
    slot_end: row.slot_end,
    stripe_customer_id: row.stripe_customer_id,
    stripe_subscription_id: row.stripe_subscription_id,
    selected_plan: row.selected_plan,
    status: row.status,
    user_id: row.user_id,
    crm_opportunity_id: row.crm_opportunity_id,
    account_id: row.account_id,
    churned_at: row.churned_at,
    current_onboarding_stage: row.current_onboarding_stage ?? 1,
    onboarding_type: (row.onboarding_type as OnboardingLead['onboarding_type']) ?? 'standard',
    first_value_achieved_at: row.first_value_achieved_at,
    go_live_at: row.go_live_at,
    internal_notes: row.internal_notes,
    owner_id: row.owner_id,
    lat: row.lat,
    lng: row.lng,
    geocoded_at: row.geocoded_at,
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
  };
}


function toMeetingReport(row: MeetingReportRow): MeetingReport {
  return {
    id: row.id,
    appointment_id: row.appointment_id,
    note_id: row.note_id,
    google_meet_conference_id: row.google_meet_conference_id,
    transcript_raw: row.transcript_raw,
    transcript_fetched_at: row.transcript_fetched_at,
    cr_title: row.cr_title,
    cr_html: row.cr_html,
    cr_markdown: row.cr_markdown,
    cr_generated_at: row.cr_generated_at,
    prompt_template_used: row.prompt_template_used,
    extracted_actions: toExtractedActions(row.extracted_actions),
    validated_actions: toExtractedActions(row.validated_actions),
    actions_validated_at: row.actions_validated_at,
    email_sent_to: row.email_sent_to,
    email_sent_at: row.email_sent_at,
    email_resend_id: row.email_resend_id,
    status: row.status as MeetingReport['status'],
    error_message: row.error_message,
    owner_id: row.owner_id ?? '',
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
  };
}

/** Backlog items of the account — one definition, see lib/backlog/featureAccounts. */
export type FeatureClientRow = AccountFeatureRow;

/**
 * A lead attached to an account, augmented with the account that actually
 * carries it, so a group head can render "via <subsidiary>". The lead↔account
 * link is resolved by the canonical `get_account_leads` RPC (account_id FK +
 * opportunity links, rolled up over the subtree) — never by name matching.
 */
export type AssociatedLead = OnboardingLead & {
  source_account_id: string | null;
  source_account_name: string | null;
  is_from_subsidiary: boolean;
  link_type: string;
};

export interface AccountDetailData {
  contacts: CRMContact[];
  notes: CRMNote[];
  opportunities: CRMOpportunity[];
  appointments: any[];
  featureClients: FeatureClientRow[];
  associatedLeads: AssociatedLead[];
  meetingReports: MeetingReport[];
}

const EMPTY_DATA: AccountDetailData = {
  contacts: [],
  notes: [],
  opportunities: [],
  appointments: [],
  featureClients: [],
  associatedLeads: [],
  meetingReports: [],
};

/**
 * Everything an account's record reads about it. The rows belong to ONE account: a late answer for
 * an account left since (a group opened from its subsidiary's sheet) is dropped, and `ready` /
 * `error` say whether the rows shown are this account's — so a screen never says « Aucun… » while
 * they load or after they failed.
 */
export function useAccountDetailData(accountId: string | null) {
  const [detailData, setDetailData] = useState<AccountDetailData>(EMPTY_DATA);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const latest = useRef<string | null>(null);

  const loadDetailData = useCallback(async (id: string) => {
    latest.current = id;
    setLoadingDetail(true);
    setError(null);
    try {
      const [contactsRes, notesRes, oppsRes, appointmentsRes, fcRes] = await Promise.all([
        supabase
          .from('crm_contacts')
          .select('*')
          .eq('account_id', id)
          .order('created_at', { ascending: false }),
        supabase
          .from('crm_notes')
          .select('*, contact:crm_contacts(id, first_name, last_name)')
          .eq('account_id', id)
          .order('is_pinned', { ascending: false })
          .order('created_at', { ascending: false }),
        supabase
          .from('crm_opportunities')
          .select('*, stage:crm_pipeline_stages(id, name, color, order_index)')
          .eq('account_id', id)
          .order('created_at', { ascending: false }),
        supabase
          .from('appointments')
          .select('*, contact:crm_contacts(id, first_name, last_name)')
          .eq('account_id', id)
          .order('start_time', { ascending: false })
          .limit(10),
        fetchAccountFeaturesOrEmpty(id),
      ]);
      // The rows a block shows: unreadable, the block says so — never an empty « Aucun… » (audit du 13/10/2026).
      const unreadable = contactsRes.error ?? notesRes.error ?? oppsRes.error ?? appointmentsRes.error;
      if (unreadable) throw new Error(unreadable.message);

      // Enrichir les notes avec celles liees aux rendez-vous du compte
      const appointmentIds = (appointmentsRes.data || []).map((a: any) => a.id).filter(Boolean);
      const allNotes: CRMNote[] = (notesRes.data || []).map(toCRMNote);
      if (appointmentIds.length > 0) {
        const { data: appointmentNotes } = await supabase
          .from('crm_notes')
          .select('*, contact:crm_contacts(id, first_name, last_name)')
          .in('appointment_id', appointmentIds);
        if (appointmentNotes) {
          const existingIds = new Set(allNotes.map(n => n.id));
          for (const note of appointmentNotes) {
            if (!existingIds.has(note.id)) {
              allNotes.push(toCRMNote(note));
            }
          }
          allNotes.sort((a, b) => {
            if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          });
        }
      }

      // Batch-fetch meeting reports for this account's notes and appointments
      const noteIds = allNotes.map(n => n.id).filter(Boolean);
      const conditions: string[] = [];
      if (appointmentIds.length > 0) {
        conditions.push(`appointment_id.in.(${appointmentIds.join(',')})`);
      }
      if (noteIds.length > 0) {
        conditions.push(`note_id.in.(${noteIds.join(',')})`);
      }
      let accountMeetingReports: MeetingReport[] = [];
      if (conditions.length > 0) {
        const { data: mrData } = await supabase
          .from('crm_meeting_reports')
          .select('*')
          .or(conditions.join(','))
          .order('created_at', { ascending: false });
        accountMeetingReports = (mrData || []).map(toMeetingReport);
      }

      const opps: CRMOpportunity[] = (oppsRes.data || []).map(toCRMOpportunity);

      // Leads du compte via la source canonique (RPC get_account_leads) :
      // rattachement par onboarding_leads.account_id + filet opportunité, avec
      // rollup du sous-arbre — une tête de groupe voit les leads de ses filiales.
      // La logique de rattachement vit dans le back : le front ne devine plus par
      // nom de société (cf. CLAUDE.md « Une règle de gestion vit dans le back »).
      const { data: leadRows, error: leadsErr } = await supabase.rpc('get_account_leads', {
        p_account_id: id,
        p_include_descendants: true,
      });
      // Optional: unreadable leads leave the account's other rows shown.
      if (leadsErr) logWarn('crm', 'account detail: leads unreadable', { accountId: id, error: leadsErr.message });
      const associatedLeads: AssociatedLead[] = (leadRows ?? []).map((r) => ({
        ...toOnboardingLead(r.lead as unknown as LeadRow),
        source_account_id: r.source_account_id,
        source_account_name: r.source_account_name,
        is_from_subsidiary: (r.depth ?? 0) > 0,
        link_type: r.link_type,
      }));

      if (latest.current !== id) return;
      setDetailData({
        contacts: (contactsRes.data || []).map(toCRMContact),
        notes: allNotes,
        opportunities: opps,
        appointments: appointmentsRes.data || [],
        featureClients: fcRes,
        associatedLeads,
        meetingReports: accountMeetingReports,
      });
      setLoadedFor(id);
    } catch (err) {
      console.error('Error loading account detail data:', err);
      if (latest.current === id) setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (latest.current === id) setLoadingDetail(false);
    }
  }, []);

  const refresh = useCallback(() => {
    if (accountId) {
      loadDetailData(accountId);
    }
  }, [accountId, loadDetailData]);

  useEffect(() => {
    // Another account: its rows are not the previous one's.
    setDetailData(EMPTY_DATA);
    setLoadedFor(null);
    if (accountId) {
      loadDetailData(accountId);
    } else {
      latest.current = null;
      setLoadingDetail(false);
    }
  }, [accountId, loadDetailData]);

  return { detailData, loadingDetail, refresh, ready: accountId !== null && loadedFor === accountId, error };
}
